import {
  CustomNode,
  Form,
  FormFiled,
  Phase,
  Project,
  TeamMember,
  Workflow,
} from '.';

export enum TaskStatus {
  InProgress = 'InProgress',
  Completed = 'Completed',
  Cancelled = 'Cancelled',
  Late = 'Late',
}

export interface Task {
  id: number;
  name: string;
  workflowName: Workflow['name'];
  workflowId: Workflow['id'];
  formName: Form['name'];
  projectName: Project['projectName'];
  phaseName: Phase['phaseName'];
  currentAssignees: TeamMember[];
  status: TaskStatus;
  isLate?: boolean;
  modifiedAt?: string;
  modifiedBy?: TeamMember;
  createdAt?: string;
  createdBy?: TeamMember;
  taskHierarchy?: {
    id: number;
    name: string;
    level: number;
    createdAt: string;
    modifiedAt: null | string;
    status: TaskStatus;
  }[];
  formFieldAnswers?: {
    id?: number;
    answer: string;
    formFieldId: number;
    title: string;
  }[];
}

export interface TaskExport {
  exportId: string;
}

export interface DetailedTask {
  createdAt: string;
  modifiedAt: null | string;
  id: number;
  name: string;
  workflow: Workflow;
  currentAssignees: TeamMember[];
  allAssignees: TeamMember[];
  status: TaskStatus;
  isLate: boolean;
  durationValue: null | string;
  durationUnit: null | string;
  dueDate: null;
  currentNode: CustomNode;
  modifiedBy: null | TeamMember;
  formFieldAnswers: {
    id: number;
    answer: string;
    formFieldId: number;
  }[];
  visitedReceivedNodes: string;
  createdBy: TeamMember;
  taskHierarchy?: {
    id: number;
    name: string;
    level: number;
    createdAt: string;
    modifiedAt: null | string;
    status: TaskStatus;
  }[];
}

export enum FiltersOperation {
  GreaterThan = 'GreaterThan',
  LessThan = 'LessThan',
  GreaterThanOrEqual = 'GreaterThanOrEqual',
  LessThanOrEqual = 'LessThanOrEqual',
  Equal = 'Equal',
  NotEqual = 'NotEqual',
  Contain = 'Contain',
}

export type PaginatedTask = {
  page: number;
  size?: number;
  phaseId?: string;
  projectId?: number;
  name?: string;
  sort?: 'asc' | 'desc';
  status: TaskStatus | undefined;
  isLate?: boolean;
  receivedFrom?: number[];
  roleAssignee?: number[];
  userAssignee?: number[];
  deadline?: string;
  forms?: {
    id: number;
    fields: { id: number; operator: FiltersOperation; answer: string }[];
  }[];
};

export type TaskFilters = {
  phaseId?: string | number[];
  projectId?: number;
  receivedFrom?: any[];
  roleAssignee?: any[];
  userAssignee?: any[];
  deadline?: string;
  forms?: {
    id: number;
    fields: {
      id: number;
      operator?: FiltersOperation;
      answer?: string | any[];
    }[];
  }[];
};

/**
 * One user-selected extra column on the tasks table: a form field, identified
 * by the form template it belongs to plus its title.
 *
 * Named here rather than re-declared inline at each site so the tasks page and
 * `TaskDynamicColumnsDrawer` are held to the SAME shape — the drawer's props
 * were passed under the wrong names for exactly as long as both ends spelled
 * this shape out separately.
 */
export type TaskDynamicColumn = {
  id?: number;
  formTemplateId: number;
  field: string;
};

export type ExportTask = {
  filters: {
    phaseId?: string;
    projectId?: number;
    name?: string;
    sort?: 'asc' | 'desc';
    status: TaskStatus | undefined;
    isLate?: boolean;
  };
  export: {
    type: 'xlxs';
    interface: string[];
  };
};

/**
 * The create-task request body. `parentTaskId` / `isChildTask` are the field
 * names the backend `TaskRequest` DTO reads — do not rename either half
 * without changing the DTO: an unknown field is dropped silently by the
 * server, which is how sub-tasks shipped with no link to their parent.
 */
export interface NewTask {
  parentTaskId?: number;
  isChildTask?: boolean;
  workflowId: Workflow['id'];
  formAnswers: {
    fieldId: FormFiled['id'];
    answer: string;
  }[];
}

export interface TaskComment {
  createdAt: string;
  modifiedAt: string | null;
  id: number;
  content: string;
  createdBy: TeamMember;
  parent: TaskComment | null;
}

export interface NewTaskComment {
  taskId: number;
  comment: string;
  parentId?: number;
}

export type TaskCommentWithReplies = TaskComment & {
  replies?: TaskComment[];
};

export interface TaskEvent {
  id: number;
  task: Task;
  by: TeamMember | null;
  on: [];
  at: string;
  action: string;
}

export interface TaskInsights {
  total: number;
  late: number;
  dueToday: number;
  taskInsightsMonthlyResponse: [
    {
      type: string;
      total: number;
      completed: number;
    }
  ];
}
